import * as Sentry from "@sentry/react";
import { browserTracingIntegration } from "@sentry/react";

// Initialize Sentry
export function initSentry() {
  // Only initialize Sentry if DSN is provided
  const dsn = import.meta.env.VITE_REACT_APP_SENTRY_DSN;
  if (!dsn) {
    console.warn('Sentry initialization skipped: DSN not found');
    return;
  }

  Sentry.init({
    dsn,
    integrations: [browserTracingIntegration()],
    
    // Performance Monitoring
    tracesSampleRate: 0.2, // Reduced to 20% to optimize for production
    
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Environment
    environment: import.meta.env.MODE || "production",
    
    // Release tracking
    release: import.meta.env.VITE_REACT_APP_VERSION || "1.0.0",
    
    // Before sending event
    beforeSend(event, hint) {
      const isProd = import.meta.env.MODE === 'production';

      // In development, just log that an error occurred but not the details
      if (!isProd) {
        console.log('Error captured in development mode - not sending to Sentry');
        return null;
      }
      
      // Filter out certain errors
      if (event.exception) {
        const exception = event.exception.values?.[0];
        if (exception?.type === "NetworkError" || exception?.type === "TypeError") {
          // Add custom filtering logic here if needed
          return event;
        }
      }
      
      return event;
    },
    
    // Ignore certain errors
    ignoreErrors: [
      /Network request failed/,
      /Failed to fetch/,
      /Loading chunk .* failed/,
      /Loading CSS chunk .* failed/,
    ],
  });
}

// Error boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Performance monitoring
export const SentryProfiler = Sentry.Profiler;

// Custom error reporting
export function captureException(error: Error, context?: any) {
  const isProd = import.meta.env.MODE === 'production';
  
  // In development, just log that an error occurred
  if (!isProd) {
    console.log('Error captured in development mode');
    return;
  }

  // Only send to Sentry in production
  Sentry.captureException(error, {
    extra: context,
  });
}

// Custom message reporting
export function captureMessage(message: string, level: Sentry.SeverityLevel = "info") {
  const isProd = import.meta.env.MODE === 'production';
  
  // In development, minimal logging
  if (!isProd) {
    console.log(`Message captured in development mode`);
    return;
  }

  Sentry.captureMessage(message, level);
}

// Set user context
export function setUserContext(user: any) {
  const isProd = import.meta.env.MODE === 'production';
  
  if (isProd && user?.id) {
    Sentry.setUser({
      id: user.id,
      // Only include email if explicitly needed for debugging
      // email: user.email,
      username: user.name,
    });
  }
}

// Set tags for better filtering
export function setTag(key: string, value: string) {
  const isProd = import.meta.env.MODE === 'production';
  if (isProd) {
    Sentry.setTag(key, value);
  }
}

// Set extra context
export function setExtra(key: string, value: any) {
  const isProd = import.meta.env.MODE === 'production';
  if (isProd) {
    // Ensure we're not logging sensitive data
    if (typeof value === 'object' && value !== null) {
      // Remove potential sensitive fields
      const sanitizedValue = { ...value };
      ['password', 'token', 'secret', 'key', 'auth', 'credentials'].forEach(field => {
        delete sanitizedValue[field];
      });
      Sentry.setExtra(key, sanitizedValue);
    } else {
      Sentry.setExtra(key, value);
    }
  }
} 